<template>
  <Transition :name="transition"
    @before-leave="setTransitioning(true)"
    @after-enter="setTransitioning(false)"
  >
    <div v-show="show"
      :id="tabPanelId"
      :aria-expanded="active ? 'true' : 'false'"
      :aria-labelledby="tabId"
      class="wizard-step"
      role="tabpanel"
      tabindex="-1"
      v-focus.preventScroll="focusTabPanel"
    >
      <slot></slot>

      <slot name="actions" v-bind="{
        isLastStep: lastStepIsActive
      }">
        <div class="wizard-actions-wrapper">
          <div>
            <NextButton />
          </div>
          <div>
            <BackButton />
          </div>
        </div>
      </slot>
    </div>
  </Transition>
</template>

<script>
import {
  mapInstanceActions as mapActions,
  mapInstanceGetters as mapGetters,
  mapInstanceMutations as mapMutations,
  mapInstanceState as mapState
} from '@urbn/vuex-helpers';
import BackButton from './BackButton';
import NextButton from './NextButton';
import { SCOPED_ID_PREFIX_KEY, STORE_NAMESPACE_KEY } from './const';
import { REGISTER_STEP } from '../../store/modules/formWizard/actions';
import { ACTIVE_STEP_NAME, LAST_STEP_IS_ACTIVE } from '../../store/modules/formWizard/getters';
import {
  MARK_STEP_COMPLETE, MARK_STEP_INCOMPLETE, MARK_STEP_VISITED, SET_TRANSITIONING
} from '../../store/modules/formWizard/mutations';
import { ACTIVE_STEP_INDEX, IS_TRANSITIONING, STEPS } from '../../store/modules/formWizard/state';

export default {
  inject: [SCOPED_ID_PREFIX_KEY, STORE_NAMESPACE_KEY],

  components: { BackButton, NextButton },

  props: {
    /**
     * A unique name to identity the step.
     */
    name: {
      type: String,
      required: true
    },

    /**
     * Once a step has been visited, may be used to specifiy whether it is
     * complete. Defaults to true so that steps are automatically marked
     * complete as soon as they are visited.
     */
    complete: {
      type: Boolean,
      default: () => true
    },

    /**
     * Specify a delay (ms) before showing the step to manage transitions.
     */
    showDelay: {
      type: Number,
      default: () => 100
    },

    /**
     * The named transition to use with Vue transitions.
     */
    transition: String
  },

  data: () => ({
    focusTabPanel: false,
    show: false
  }),

  computed: {
    ...mapGetters((vm) => vm[STORE_NAMESPACE_KEY], {
      activeStepName: ACTIVE_STEP_NAME,
      lastStepIsActive: LAST_STEP_IS_ACTIVE
    }),

    ...mapState((vm) => vm[STORE_NAMESPACE_KEY], [
      ACTIVE_STEP_INDEX,
      IS_TRANSITIONING,
      STEPS
    ]),

    active: (vm) => vm.activeStepName === vm.name,

    tabId() {
      return `${this[SCOPED_ID_PREFIX_KEY]}-tab-${this.stepIndex}`;
    },

    tabPanelId() {
      return `${this[SCOPED_ID_PREFIX_KEY]}-tabpanel-${this.stepIndex}`;
    },

    stepIndex: (vm) => vm[STEPS].findIndex((s) => s.name === vm.name)
  },

  watch: {
    active: {
      immediate: true,
      handler(val) {
        setTimeout(() => {
          // Use the current value in case it has changed since the timeout began.
          this.show = this.active;
        }, val ? this.showDelay : 0);

        if (val) {
          this.markStepVisited(this.stepIndex);

          // When step becomes active, automatically mark it complete unless specifically
          // prevented by "complete" prop.
          if (this.complete) {
            this.markStepComplete(this.stepIndex);
          }
        }
      }
    },

    [ACTIVE_STEP_INDEX](val, oldVal) {
      if ([val, oldVal].includes(this.stepIndex)) {
        this.$emit(`move-${val < oldVal ? 'back' : 'forward'}`);
      }
    },

    complete(val, oldVal) {
      if (val !== oldVal) this[val ? 'markStepComplete' : 'markStepIncomplete'](this.stepIndex);
    },

    [IS_TRANSITIONING](val) {
      if (!val && this.active) this.focusTabPanel = true;
    }
  },

  methods: {
    ...mapActions((vm) => vm[STORE_NAMESPACE_KEY], [
      REGISTER_STEP
    ]),

    ...mapMutations((vm) => vm[STORE_NAMESPACE_KEY], {
      markStepComplete: MARK_STEP_COMPLETE,
      markStepIncomplete: MARK_STEP_INCOMPLETE,
      markStepVisited: MARK_STEP_VISITED,
      setTransitioning: SET_TRANSITIONING
    })
  },

  mounted() {
    this[REGISTER_STEP](this.name);
  }
};
</script>

<style lang="scss" scoped>
.wizard-step {
  outline: none;
}

.wizard-actions-wrapper {
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
}
</style>
